import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getObstacleIcon, getArpIcon, getShieldComboIcon } from '../../utils/markers';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addHullPolygonsToMap, getObstaclesToRender } from '../../utils/hullMapLayer';
import { T } from '../../assets/theme';
import { useSurfaceStyles } from '../../assets/SurfaceStyleContext';
import { getOLSSurfaces, assessOlsSingle } from '../../api/client';
import { DraggableModal, ResizeHandle } from '../../components/UI';
import { useResizableHeight } from '../../utils/useResizableHeight';
import { useShield, exportSurfacePenetratingCsv } from '../../utils/useShield';
import ShieldControl from '../../components/ShieldControl';
import MapMeasureTool from '../../components/MapMeasureTool';
import MapCoordinateViewer from '../../components/MapCoordinateViewer';
import KmlExportModal from '../../components/KmlExportModal';
import IhsThresholdModal from '../../components/IhsThresholdModal';
import OlsParametersModal from '../../components/OlsParametersModal';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

function toDMS(deg, isLat) {
    if (deg == null || isNaN(deg)) return '—';
    const abs = Math.abs(deg);
    const d = Math.floor(abs);
    const mFull = (abs - d) * 60;
    const m = Math.floor(mFull);
    const s = ((mFull - m) * 60).toFixed(4);
    const dir = isLat ? (deg >= 0 ? 'N' : 'S') : (deg >= 0 ? 'E' : 'W');
    return `${d}° ${m}' ${s}" ${dir}`;
}

const exportToCsv = (headers, rows, filename) => {
    const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(val => {
            const cleanVal = String(val ?? '').replace(/—/g, '-');
            return `"${cleanVal.replace(/"/g, '""')}"`;
        }).join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TILE_LAYERS = {
    dark: { label: '🌙 Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CARTO' },
    street: { label: '🗺 Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OSM' },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri' },
};

// Surface colours matching QGIS OLS Tool — these are just defaults; the
// derived `surfColors` built inside the component (via getStyle('ols.*'))
// is what's actually used for rendering, so Settings customization applies.
const DEFAULT_SURF_COLORS = {
    ihs: { color: '#3A8AC4', fill: '#3A8AC4', label: 'IHS' },
    conical: { color: '#F5A623', fill: '#F5A623', label: 'Conical' },
    ohs: { color: '#9B59B6', fill: '#9B59B6', label: 'OHS' },
    strip: { color: '#2EC98B', fill: '#2EC98B', label: 'Strip' },
    approach: { color: '#F0C040', fill: '#F0C040', label: 'Approach' },
    departure: { color: '#E84040', fill: '#E84040', label: 'Take-off' },
    transitional: { color: '#1ABC9C', fill: '#1ABC9C', label: 'Transitional' },
    inner_approach: { color: '#FF9F43', fill: '#FF9F43', label: 'Inner Approach' },
    balked_landing: { color: '#EE5253', fill: '#EE5253', label: 'Balked Landing' },
    inner_transitional: { color: '#00D2D3', fill: '#00D2D3', label: 'Inner Transitional' },
};

const AERODROME_SURFACES = ['ihs', 'conical', 'ohs'];
const OFZ_SURFACES = ['inner_approach', 'balked_landing', 'inner_transitional'];
// Helper: normalize runway designator for comparison (strip leading zeros)
const normDesig = s => String(parseInt(String(s).trim(), 10));


function StatusBadge({ status }) {
    const colors = { penetrated: T.danger, clear: T.safe, outside: T.muted, 'N/A': T.muted };
    const c = colors[status] || T.muted;
    return (
        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, color: '#fff', background: c + '33', border: `1px solid ${c}55` }}>
            {(status || '—').toUpperCase()}
        </span>
    );
}

// Build default visibility state for an ICAO
function defaultVis() {
    return {
        transitional: true,  // aerodrome-wide: all pairs' transitional surfaces
        transDebug: false,   // F/G2/G/H/H2/C construction points for the transitional surface
        ihs: true, conical: true, ohs: true,
        // pairs: { [pairId]: { strip, [desig1]: { approach, departure }, [desig2]: { approach, departure }, ofz: {...} } }
        pairs: {},
    };
}

// Transitional surface ring order (see _transitional() in ols_surfaces.py):
// [F, G2, G, H, H2, C, ...outer D-E points, F(closing)] — the first 6 coords
// of each LEFT/RIGHT feature's ring are always these named construction
// points, in this order.
const TRANS_DEBUG_LABELS = [
    { key: 'F',  desc: 'Approach outer corner (T1 side) — where transitional caps at IHS height' },
    { key: 'G2', desc: "Approach surface's own inner corner (T1 side)" },
    { key: 'G',  desc: 'Strip inner corner (T1 side)' },
    { key: 'H',  desc: 'Strip inner corner (T2 side)' },
    { key: 'H2', desc: "Approach surface's own inner corner (T2 side)" },
    { key: 'C',  desc: 'Approach outer corner (T2 side) — where transitional caps at IHS height' },
];

export default function OLSPage({
    project, arps, obstacles, loading,
    surfaceData, setSurfaceData,
    vis, setVis,
    expanded, setExpanded,
    capturedIcaos, setCapturedIcaos,
    showObstacles = true, showObsLabels, showArpLabels, onObstacleContextMenu, onMapReady, onAerodromeContextMenu,
}) {
    const projectId = project?.id;
    const { getStyle } = useSurfaceStyles();
    const surfColors = useMemo(() => {
        const out = {};
        Object.entries(DEFAULT_SURF_COLORS).forEach(([key, cfg]) => {
            const s = getStyle(`ols.${key}`);
            out[key] = { ...cfg, color: s.color, fill: s.color, opacity: s.opacity };
        });
        return out;
    }, [getStyle]);
    const mapRef = useRef(null);
    const mapInst = useRef(null);
    const tileRef = useRef(null);
    const surfLayersRef = useRef([]);   // GeoJSON surface layers
    const obsLayersRef = useRef([]);   // obstacle marker layers
    const arpMarkersRef = useRef({});  // icao → circleMarker
    const prevSurfKeysRef = useRef(''); // track loaded icaos to detect new loads
    const deptShieldLayerRef = useRef(null); // real inclined-OLS-surface shield markers
    const comboShieldLayerRef = useRef(null); // half-red/half-yellow halo: red (raw-elevation) AND genuinely penetrates a real surface

    const [mapMode, setMapMode] = useState('street');
    const [mapState, setMapState] = useState(null);
    const [loadingIcao, setLoadingIcao] = useState({});
    const [errorIcao, setErrorIcao] = useState({});
    const [olsTab, setOlsTab] = useState('ALL');
    // Per-ICAO local assessment results { [icao]: rows[] }
    const [localOlsResults, setLocalOlsResults] = useState({});
    const [assessingIcao, setAssessingIcao] = useState({});
    const [showKmlExport, setShowKmlExport] = useState(false);
    const [showIhsThreshold, setShowIhsThreshold] = useState(false);
    const [showOlsParameters, setShowOlsParameters] = useState(false);
    const [exportingAixm, setExportingAixm] = useState(false);
    const [resetIcao, setResetIcao] = useState(null);
    const [resultsCollapsed, setResultsCollapsed] = useState(false);
    const [resultsHeight, startResultsResize] = useResizableHeight(205, { min: 120, max: 700 });
    const [resultsPoppedOut, setResultsPoppedOut] = useState(false);

    useEffect(() => {
        if (mapInst.current) {
            setTimeout(() => { mapInst.current?.invalidateSize(); }, 200);
        }
    }, [resultsCollapsed, resultsPoppedOut]);

    const validArps = React.useMemo(() => {
        const DEG = Math.PI / 180;
        const R = 6371000;
        return arps.filter(arp => {
            if (!arp.lat || !arp.lon) return false;
            for (const obs of obstacles) {
                if (!obs.latitude || !obs.longitude) continue;
                const dLat = (obs.latitude - arp.lat) * DEG;
                const dLon = (obs.longitude - arp.lon) * DEG;
                const a = Math.sin(dLat / 2) ** 2 + Math.cos(arp.lat * DEG) * Math.cos(obs.latitude * DEG) * Math.sin(dLon / 2) ** 2;
                if (R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) <= 15000) return true;
            }
            return false;
        });
    }, [arps, obstacles]);

    // Init map
    useEffect(() => {
        if (!mapRef.current || mapInst.current) return;
        mapInst.current = L.map(mapRef.current, {
            center: [-28.5, 25.0],
            zoom: 6,
            zoomDelta: 0.35,
            zoomSnap: 0.35,
            wheelPxPerZoomLevel: 120
        });
        tileRef.current = L.tileLayer(TILE_LAYERS[mapMode].url, { attribution: TILE_LAYERS[mapMode].attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
        // ResizeObserver keeps the SVG pane aligned whenever the flex container
        // changes size (initial layout, tab switches, window resize, etc.).
        const ro = new ResizeObserver(() => { mapInst.current?.invalidateSize(); });
        ro.observe(mapRef.current);
        setMapState(mapInst.current);
        onMapReady?.(mapInst.current);
        return () => { ro.disconnect(); mapInst.current?.remove(); mapInst.current = null; setMapState(null); onMapReady?.(null); };
    }, []);

    // Tile layer switch
    useEffect(() => {
        if (!mapInst.current) return;
        if (tileRef.current) mapInst.current.removeLayer(tileRef.current);
        const l = TILE_LAYERS[mapMode];
        tileRef.current = L.tileLayer(l.url, { attribution: l.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    // ── Load surfaces + assess for a single aerodrome ──────────────────────────
    const loadSurfaces = useCallback(async (icao) => {
        setLoadingIcao(p => ({ ...p, [icao]: true }));
        setErrorIcao(p => ({ ...p, [icao]: null }));
        try {
            const r = await getOLSSurfaces(icao);
            const data = r.data;
            setSurfaceData(p => ({ ...p, [icao]: data }));

            // Build per-designator visibility
            const v = defaultVis();
            (data.runway_pairs || []).forEach(pair => {
                const pv = { strip: true, ofz: {} };
                [pair.desig1, pair.desig2].filter(Boolean).forEach(d => {
                    pv[d] = { approach: true, departure: true };
                });
                if (pair.surfaces?.ofz) {
                    Object.keys(pair.surfaces.ofz).forEach(desig => {
                        pv.ofz[desig] = { inner_approach: true, balked_landing: true, inner_transitional: true };
                    });
                }
                v.pairs[pair.pair_id] = pv;
            });
            setVis(p => ({ ...p, [icao]: v }));
            setExpanded(p => ({ ...p, [icao]: true }));

            // Run assessment for this aerodrome only
            setAssessingIcao(p => ({ ...p, [icao]: true }));
            try {
                const aRes = await assessOlsSingle(icao, obstacles);
                setLocalOlsResults(p => ({ ...p, [icao]: aRes.data.results || [] }));
            } catch (ae) {
                console.error(`OLS assess failed for ${icao}:`, ae);
            } finally {
                setAssessingIcao(p => ({ ...p, [icao]: false }));
            }
        } catch (e) {
            setErrorIcao(p => ({ ...p, [icao]: e.response?.data?.detail || 'Failed to load surfaces.' }));
        } finally {
            setLoadingIcao(p => ({ ...p, [icao]: false }));
        }
    }, [obstacles]); // eslint-disable-line

    // ── Render surface GeoJSON layers on map ─────────────────────────────────
    useEffect(() => {
        if (!mapInst.current) return;

        // Remove old surface layers
        surfLayersRef.current.forEach(l => mapInst.current.removeLayer(l));
        surfLayersRef.current = [];

        const bounds = [];

        Object.entries(surfaceData).forEach(([icao, data]) => {
            const v = vis[icao] || {};

            // ── Aerodrome-level surfaces ──────────────────────────────────────
            AERODROME_SURFACES.forEach(key => {
                if (!v[key]) return;
                const geom = data.aerodrome_surfaces?.[key];
                if (!geom) return;
                const cfg = surfColors[key];
                try {
                    const layer = L.geoJSON(geom, {
                        style: { color: cfg.color, weight: 1.5, fillColor: cfg.fill, fillOpacity: cfg.opacity, dashArray: key === 'ohs' ? '8 4' : null },
                    }).bindTooltip(`${icao} – ${cfg.label}`);
                    layer.addTo(mapInst.current);
                    surfLayersRef.current.push(layer);
                    try { const b = layer.getBounds(); if (b.isValid()) bounds.push(b.getCenter()); } catch { }
                } catch { }
            });

            // ── Pair-level surfaces ───────────────────────────────────────────
            (data.runway_pairs || []).forEach(pair => {
                const pv = v.pairs?.[pair.pair_id] || {};

                // Strip (one polygon covering the whole runway)
                if (pv.strip && pair.surfaces?.strip) {
                    const cfg = surfColors.strip;
                    try {
                        const layer = L.geoJSON(pair.surfaces.strip, {
                            style: { color: cfg.color, weight: 1.5, fillColor: cfg.fill, fillOpacity: cfg.opacity },
                            onEachFeature: (f, fl) => fl.bindTooltip(`${icao} – ${f.properties?.name || 'Strip'}`),
                        });
                        layer.addTo(mapInst.current);
                        surfLayersRef.current.push(layer);
                        try { const b = layer.getBounds(); if (b.isValid()) bounds.push(b.getCenter()); } catch { }
                    } catch { }
                }

                // Transitional — controlled by the aerodrome-level toggle
                if (v.transitional && pair.surfaces?.transitional) {
                    const cfg = surfColors.transitional;
                    try {
                        const layer = L.geoJSON(pair.surfaces.transitional, {
                            style: { color: cfg.color, weight: 1.5, fillColor: cfg.fill, fillOpacity: cfg.opacity },
                            onEachFeature: (f, fl) => fl.bindTooltip(`${icao} – ${f.properties?.name || 'Transitional'}`),
                        });
                        layer.addTo(mapInst.current);
                        surfLayersRef.current.push(layer);
                        try { const b = layer.getBounds(); if (b.isValid()) bounds.push(b.getCenter()); } catch { }
                    } catch { }

                    // Construction-point debug overlay — plots the named
                    // F/G2/G/H/H2/C vertices straight off the same GeoJSON
                    // ring the surface polygon itself was built from (see
                    // TRANS_DEBUG_LABELS), so what's shown always matches
                    // exactly what the backend actually constructed.
                    if (v.transDebug) {
                        (pair.surfaces.transitional.features || []).forEach(f => {
                            const ring = f.geometry?.coordinates?.[0] || [];
                            const side = f.properties?.side || '';
                            TRANS_DEBUG_LABELS.forEach((info, i) => {
                                const coord = ring[i];
                                if (!coord) return;
                                const [lon, lat] = coord;
                                const m = L.circleMarker([lat, lon], {
                                    radius: 5, color: '#fff', weight: 2,
                                    fillColor: '#FF3B9A', fillOpacity: 1,
                                }).bindTooltip(
                                    `${icao} ${pair.rwy1 || pair.desig1}/${pair.rwy2 || pair.desig2} · ${side}<br/><b>${info.key}</b><br/>${info.desc}<br/>${lat.toFixed(6)}, ${lon.toFixed(6)}`,
                                    { permanent: false, direction: 'top' }
                                );
                                const label = L.marker([lat, lon], {
                                    icon: L.divIcon({
                                        className: '',
                                        html: `<div style="white-space:nowrap; padding-left:9px; font-size:10px; font-weight:700; color:#FF3B9A; text-shadow:1px 1px 2px #000,-1px -1px 2px #000,1px -1px 2px #000,-1px 1px 2px #000;">${info.key}</div>`,
                                        iconSize: [60, 14], iconAnchor: [0, 7],
                                    }),
                                    interactive: false,
                                });
                                m.addTo(mapInst.current);
                                label.addTo(mapInst.current);
                                surfLayersRef.current.push(m);
                                surfLayersRef.current.push(label);
                            });
                        });
                    }
                }

                // Approach and Departure — per designator, filter features by name
                [pair.desig1, pair.desig2].filter(Boolean).forEach(desig => {
                    const dv = pv[desig] || {};
                    ['approach', 'departure'].forEach(key => {
                        if (!dv[key]) return;
                        const geom = pair.surfaces?.[key];
                        if (!geom) return;
                        const cfg = surfColors[key];
                        try {
                            const layer = L.geoJSON(geom, {
                                style: { color: cfg.color, weight: 1.5, fillColor: cfg.fill, fillOpacity: cfg.opacity },
                                filter: (feature) => {
                                    const fname = (feature.properties?.name || '').trim();
                                    const last = fname.split(' ').pop();
                                    return normDesig(last) === normDesig(desig);
                                },
                                onEachFeature: (f, fl) => fl.bindTooltip(`${icao} – ${f.properties?.name || cfg.label}`),
                            });
                            layer.addTo(mapInst.current);
                            surfLayersRef.current.push(layer);
                            try { const b = layer.getBounds(); if (b.isValid()) bounds.push(b.getCenter()); } catch { }
                        } catch { }
                    });
                });

                // ── OFZ surfaces ─────────────────────────────────────────────
                if (pair.surfaces?.ofz) {
                    Object.entries(pair.surfaces.ofz).forEach(([desig, surfs]) => {
                        const ov = pv.ofz?.[desig] || {};
                        OFZ_SURFACES.forEach(key => {
                            if (!ov[key]) return;
                            const geom = surfs[key];
                            if (!geom) return;
                            const cfg = surfColors[key];
                            try {
                                const layer = L.geoJSON(geom, {
                                    style: { color: cfg.color, weight: 1.5, fillColor: cfg.fill, fillOpacity: cfg.opacity },
                                }).bindTooltip(`${icao} RWY ${desig} OFZ – ${cfg.label}`);
                                layer.addTo(mapInst.current);
                                surfLayersRef.current.push(layer);
                                try { const b = layer.getBounds(); if (b.isValid()) bounds.push(b.getCenter()); } catch { }
                            } catch { }
                        });
                    });
                }
            });
        });

        // Only zoom when new aerodromes are loaded, not on checkbox toggle (vis change)
        const currentKeys = Object.keys(surfaceData).sort().join(',');
        if (bounds.length && currentKeys !== prevSurfKeysRef.current) {
            prevSurfKeysRef.current = currentKeys;
            mapInst.current.fitBounds(L.latLngBounds(bounds), { padding: [60, 60], maxZoom: 12 });
        }
    }, [surfaceData, vis]);

    // ── Render ARP + obstacle markers ─────────────────────────────────────────
    useEffect(() => {
        if (!mapInst.current || loading) return;

        Object.values(arpMarkersRef.current).forEach(m => mapInst.current.removeLayer(m));
        arpMarkersRef.current = {};
        obsLayersRef.current.forEach(l => mapInst.current.removeLayer(l));
        obsLayersRef.current = [];

        const bounds = [];

        validArps.forEach(arp => {
            if (!arp.lat || !arp.lon) return;
            const ll = [arp.lat, arp.lon];
            bounds.push(ll);
            const m = L.marker(ll, { icon: getArpIcon(L, arp, showArpLabels) })
                .bindPopup(`<b>✈ ${arp.icao}</b><br/>${arp.name}<br/>${arp.distance_km} km`);
            if (onAerodromeContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onAerodromeContextMenu(arp, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(mapInst.current);
            arpMarkersRef.current[arp.icao] = m;
        });

        const penetratingIds = new Set(
            Object.values(localOlsResults).flat()
                .filter(r => r.status === 'penetrated')
                .map(r => r.obstacle_id)
        );
        getObstaclesToRender(obstacles.filter(o => o.latitude && o.longitude), project?.id, showObstacles).forEach(o => {
            const ll = [o.latitude, o.longitude];
            bounds.push(ll);
            const isPen = penetratingIds.has(o.id);
            const icon = getObstacleIcon(L, o, isPen ? 'penetrated' : 'clear', showObsLabels);

            const obsResults = Object.values(localOlsResults).flat().filter(r => Number(r.obstacle_id) === Number(o.id));
            let penetrationStr = '—';
            let surfaceStr = '—';
            // Strip surfaces report margin: null (Annex 14 6.3.1 — the strip must
            // be entirely free of objects, so there's no elevation/margin to
            // compute; being inside it is an automatic, absolute violation).
            // Treat that as worse than any numeric penetration, not as "no data"
            // (which would otherwise rank it below even a clear surface).
            const penOf = r => r.margin != null ? -r.margin : (r.status === 'penetrated' ? Infinity : -999999);
            if (obsResults.length > 0) {
                let worstRes = obsResults[0];
                let maxPen = penOf(worstRes);
                for (let i = 1; i < obsResults.length; i++) {
                    const r = obsResults[i];
                    const pen = penOf(r);
                    if (pen > maxPen) {
                        maxPen = pen;
                        worstRes = r;
                    }
                }
                if (maxPen > -999999) {
                    if (maxPen === Infinity) {
                        penetrationStr = 'Penetrated (Strip)';
                    } else if (maxPen > 0) {
                        penetrationStr = `${maxPen.toFixed(2)} m`;
                    } else {
                        penetrationStr = `${maxPen.toFixed(2)} m (Clear)`;
                    }
                    surfaceStr = worstRes.surface || '—';
                }
            }

            const marker = L.marker(ll, { icon })
                .bindPopup(`<b>${o.name}</b><br/>ID: ${o.id}<br/>ELEVATION : ${o.elevation_m ?? '—'} m<br/>SURFACE: ${surfaceStr}<br/>PENETRATION: ${penetrationStr}`);
            if (onObstacleContextMenu) {
                marker.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onObstacleContextMenu(o, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            marker.addTo(mapInst.current);
            obsLayersRef.current.push(marker);
        });
        addHullPolygonsToMap(mapInst.current, obstacles, project?.id, obsLayersRef);

        if (bounds.length && surfLayersRef.current.length === 0) {
            mapInst.current.fitBounds(L.latLngBounds(bounds), { padding: [60, 60], maxZoom: 12 });
        }
    }, [validArps, obstacles, loading, showObstacles, showObsLabels, showArpLabels, localOlsResults, onObstacleContextMenu, onAerodromeContextMenu, surfColors]);

    // ── Shielding — existing SWOT/SOF obstacles within 15km of the project's hull ──
    // "Shielding principle": where there's no published minima to lean on, an
    // existing obstacle already higher than anything in the application proves
    // the application isn't a NEW impact — the surface is already violated by
    // something already there.
    // Step 2 reference set — this project's own obstacles that actually
    // penetrate something (any surface), not just its tallest point overall.
    const penetratingObstacleIds = useMemo(() => (
        Object.values(localOlsResults).flat()
            .filter(r => r.status === 'penetrated')
            .map(r => r.obstacle_id)
    ), [localOlsResults]);
    const { shieldOn, toggleShield, shieldData, shieldLoading, shieldError, projectMaxTotalElev, projectPenetratingMaxTotalElev, radiusKm, setRadiusKm, exportCsv, shieldLayerRef } = useShield(mapInst, obstacles, penetratingObstacleIds);

    // ── Real per-surface OLS shielding — not just flat elevation ────────────────
    // Departure, Approach, Conical, and Transitional are sloped: a shorter
    // obstacle close to the reference line/point can force a steeper
    // requirement than a taller one further out, so "shields" for those has
    // to mean "already penetrates THIS surface, at its own position, by at
    // least as much as the project's own worst obstacle does" — not "is
    // taller". IHS/OHS ARE flat (a fixed height above ARP), but that doesn't
    // make the project-max/project-worst elevation comparison a correct
    // proxy for them either — a candidate can sit below the project's own
    // (very tall) obstacles yet still be above the IHS/OHS's own absolute
    // height, or vice versa. So all six get the same real-per-point check.
    // Computed by running each SWOT/SOF candidate through the exact same
    // per-surface geometry engine used for real project obstacles
    // (assess/{icao}, i.e. assessOlsSingle) so this can never diverge from
    // what the real OLS assessment says about that point. One call already
    // returns every surface type a candidate hits, so all six are covered
    // for the cost of the one lookup the departure-only version already made.
    const INCLINED_OLS_SURFACES = ['departure', 'approach', 'conical', 'transitional', 'ihs', 'ohs'];
    const OLS_SURFACE_LABELS = { departure: 'Departure (Take-off Climb)', approach: 'Approach', conical: 'Conical', transitional: 'Transitional', ihs: 'Inner Horizontal (IHS)', ohs: 'Outer Horizontal (OHS)' };
    const [olsShieldPens, setOlsShieldPens] = useState({}); // icao -> candidate result rows (inclined surfaces only)
    const [olsShieldLoading, setOlsShieldLoading] = useState(false);

    // Only worth checking when the project's OWN obstacles already show a
    // penetration on one of these surfaces somewhere — otherwise there's no
    // impact to shield in the first place.
    const olsSurfaceImpacts = useMemo(() => {
        const out = {}; // `${surfaceType}_${icao}_${runway||'OMNI'}` -> worst (most negative) margin
        Object.entries(localOlsResults).forEach(([icao, rows]) => {
            (rows || []).forEach(r => {
                if (!INCLINED_OLS_SURFACES.includes(r.surfaceType) || r.status !== 'penetrated' || r.margin == null) return;
                const key = `${r.surfaceType}_${icao}_${r.runway || 'OMNI'}`;
                if (!(key in out) || r.margin < out[key]) out[key] = r.margin;
            });
        });
        return out;
    }, [localOlsResults]);
    const hasOlsInclinedImpact = Object.keys(olsSurfaceImpacts).length > 0;

    useEffect(() => {
        if (!shieldOn || !shieldData || !hasOlsInclinedImpact) { setOlsShieldPens({}); return; }
        const icaos = [...new Set(Object.keys(olsSurfaceImpacts).map(k => k.split('_')[1]))];
        if (!icaos.length) return;

        const candidates = [
            ...(shieldData.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
            ...(shieldData.sof || []).map(r => ({ ...r, kind: 'SOF' })),
        ].filter(r => r.lat != null && r.lon != null);
        if (!candidates.length) { setOlsShieldPens({}); return; }

        const pseudoObstacles = candidates.map(r => ({
            id: `shield_${r.kind}_${r.ogc_fid}`,
            name: r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type),
            latitude: r.lat, longitude: r.lon,
            elevation_m: parseFloat(r.elev_m) || 0,
            v_tolerance: parseFloat(r.v_tolerance_m) || 0,
            h_tolerance: 0,
        }));

        let cancelled = false;
        (async () => {
            setOlsShieldLoading(true);
            const perIcao = {};
            for (const icao of icaos) {
                try {
                    const { data } = await assessOlsSingle(icao, pseudoObstacles);
                    perIcao[icao] = (data.results || []).filter(r => INCLINED_OLS_SURFACES.includes(r.surfaceType));
                } catch (e) {
                    console.error(`OLS inclined-surface shield check failed for ${icao}`, e);
                }
            }
            if (!cancelled) setOlsShieldPens(perIcao);
            setOlsShieldLoading(false);
        })();
        return () => { cancelled = true; };
    }, [shieldOn, shieldData, hasOlsInclinedImpact, olsSurfaceImpacts]);

    // Candidate pseudo-id -> [{ surfaceType, icao, runway, margin, projectMargin }, ...]
    // — a candidate can shield more than one surface at once (e.g. Approach
    // and Conical both), so this is a list per candidate, not a single verdict.
    const olsShieldVerdicts = useMemo(() => {
        const out = new Map();
        Object.entries(olsShieldPens).forEach(([icao, rows]) => {
            rows.forEach(r => {
                if (r.margin == null) return;
                const key = `${r.surfaceType}_${icao}_${r.runway || 'OMNI'}`;
                const projectMargin = olsSurfaceImpacts[key];
                if (projectMargin == null) return; // project doesn't penetrate this surface/runway
                if (r.margin <= projectMargin) {
                    const list = out.get(r.obstacle_id) || [];
                    list.push({ surfaceType: r.surfaceType, icao, runway: r.runway, margin: r.margin, projectMargin });
                    out.set(r.obstacle_id, list);
                }
            });
        });
        return out;
    }, [olsShieldPens, olsSurfaceImpacts]);

    // Candidate pseudo-id -> true if it falls under ANY inclined surface's
    // footprint at all (has a real margin there), regardless of whether that
    // margin was bad enough to actually shield. Lets the popup say plainly
    // "Outside surface? Yes/No" instead of folding two different reasons
    // ("never near a surface" vs "near it but not penetrating enough") into
    // one vague sentence.
    const olsShieldInsideSurface = useMemo(() => {
        const out = new Map();
        Object.values(olsShieldPens).forEach(rows => {
            rows.forEach(r => { if (r.margin != null) out.set(r.obstacle_id, true); });
        });
        return out;
    }, [olsShieldPens]);

    // Candidate pseudo-id -> [{ surfaceType, runway, margin }, ...] for every
    // surface it genuinely penetrates at its own position (margin < 0) —
    // independent of whether it's also taller than the project's own worst
    // point (the red/grey check) or beats the project's worst margin closely
    // enough to count as a "true shield" (the amber check, olsShieldVerdicts).
    // A shorter obstacle that still dips below a sloped surface here is real,
    // useful evidence the flat elevation comparison misses entirely —
    // flagged yellow so it's visually distinct from both, and named so it's
    // clear WHICH surface, not just that one exists.
    const olsShieldRealPenetration = useMemo(() => {
        const out = new Map();
        Object.values(olsShieldPens).forEach(rows => {
            rows.forEach(r => {
                if (r.margin == null || r.margin >= 0) return;
                const list = out.get(r.obstacle_id) || [];
                list.push({ surfaceType: r.surfaceType, runway: r.runway, margin: r.margin });
                out.set(r.obstacle_id, list);
            });
        });
        return out;
    }, [olsShieldPens]);

    // Enrich each shield marker's own popup (from useShield) with which real
    // surface(s) it verifies against — a flat "taller" verdict there doesn't
    // by itself mean an obstacle shields an inclined surface, so this states
    // the actual per-surface outcome(s) (or that none apply) right where the
    // user already looks. Markers are tagged by useShield with a stable key
    // and their own base HTML so this can rebuild cleanly on every update
    // rather than risking duplicated/stale appended text. Also recolours the
    // marker yellow when it genuinely penetrates a real surface but wasn't
    // already red (raw-elevation shield) — a third state alongside the
    // existing red/grey.
    useEffect(() => {
        const group = shieldLayerRef.current;
        if (comboShieldLayerRef.current) { mapInst.current?.removeLayer(comboShieldLayerRef.current); comboShieldLayerRef.current = null; }
        if (!group || !hasOlsInclinedImpact) return;
        const comboGroup = L.featureGroup();
        group.eachLayer(layer => {
            if (!layer._shieldKey || !layer._basePopupHtml) return;
            const verdicts = olsShieldVerdicts.get(layer._shieldKey);
            const insideAnySurface = olsShieldInsideSurface.get(layer._shieldKey) || false;
            const realPenList = olsShieldRealPenetration.get(layer._shieldKey) || [];
            const realPenetration = realPenList.length > 0;
            const realPenNames = [...new Set(realPenList.map(v => OLS_SURFACE_LABELS[v.surfaceType] + (v.runway ? ` RWY ${v.runway}` : '')))];
            const outsideLine = `<br/><span style="color:#8a94a6">Outside surface?: ${insideAnySurface ? 'No' : 'Yes'}</span>`;
            const penetratesLine = realPenetration
                ? `<br/><span style="color:#2D9CDB">Penetrates real surface (regardless of height)?: Yes — ${realPenNames.join(', ')}</span>`
                : `<br/><span style="color:#8a94a6">Penetrates real surface (regardless of height)?: No</span>`;
            const surfacesLine = (verdicts && verdicts.length)
                ? verdicts.map(v =>
                    `<br/><b style="color:#FFB020">Inclined OLS surfaces: ${OLS_SURFACE_LABELS[v.surfaceType]}</b>${v.runway ? ` RWY ${v.runway}` : ''} ` +
                    `(this obstacle's margin ${v.margin.toFixed(1)} m vs project's worst ${v.projectMargin.toFixed(1)} m)`
                  ).join('')
                : `<br/><span style="color:#8a94a6">Inclined OLS surfaces: No</span>`;
            layer.setPopupContent(layer._basePopupHtml + outsideLine + penetratesLine + surfacesLine);
            if (realPenetration && !layer._shieldsRaw) {
                layer.setStyle({ color: '#FFE14D', fillColor: '#FFE14D', weight: 2, radius: 7, fillOpacity: 0.85 });
            } else if (realPenetration && layer._shieldsRaw) {
                // Both true at once: red (raw-elevation shield) AND a
                // confirmed real-surface penetration — a half-red/half-yellow
                // halo on top of the base red marker makes both facts visible
                // instead of one silently winning.
                const halo = L.marker(layer.getLatLng(), { icon: getShieldComboIcon(L), interactive: false });
                comboGroup.addLayer(halo);
            }
        });
        if (comboGroup.getLayers().length > 0) {
            comboShieldLayerRef.current = comboGroup;
            comboGroup.addTo(mapInst.current);
        }
    }, [shieldLayerRef, olsShieldVerdicts, olsShieldInsideSurface, olsShieldRealPenetration, hasOlsInclinedImpact, shieldData, shieldOn]);

    // Draw a distinct marker for every candidate confirmed to truly shield at
    // least one inclined OLS surface — separate layer from useShield's own
    // generic (flat-elevation) markers, so the real per-surface verdict is
    // visually unambiguous rather than just recoloring the existing marker.
    useEffect(() => {
        if (!mapInst.current) return;
        if (deptShieldLayerRef.current) { mapInst.current.removeLayer(deptShieldLayerRef.current); deptShieldLayerRef.current = null; }
        if (!shieldOn || olsShieldVerdicts.size === 0) return;

        const group = L.featureGroup();
        const byId = new Map();
        [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
         ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
            byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
        });

        olsShieldVerdicts.forEach((verdicts, obsId) => {
            const r = byId.get(obsId);
            if (!r || r.lat == null || r.lon == null) return;
            const marker = L.circleMarker([r.lat, r.lon], {
                radius: 10, color: '#FFB020', weight: 3, fillColor: '#FFB020', fillOpacity: 0.35,
            });
            const label = r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type);
            const lines = verdicts.map(v =>
                `<b>${OLS_SURFACE_LABELS[v.surfaceType]}</b>${v.runway ? ` RWY ${v.runway}` : ''} — ` +
                `margin <b>${v.margin.toFixed(1)} m</b> (project worst: ${v.projectMargin.toFixed(1)} m)`
            ).join('<br/>');
            marker.bindPopup(
                `<b>🛫 TRUE SHIELD</b><br/>${r.kind}: ${label}<br/>ID: ${r.caa_id || '—'} (row ${r.ogc_fid})<br/>` +
                `Aerodrome: ${verdicts[0].icao}<br/>${lines}<br/>` +
                `<span style="color:#FFB020">Already penetrates as much or more — the project is not a new impact on ${verdicts.length > 1 ? 'these surfaces' : 'this surface'}.</span>`
            );
            group.addLayer(marker);
        });

        if (group.getLayers().length > 0) {
            deptShieldLayerRef.current = group;
            group.addTo(mapInst.current);
        }
    }, [shieldOn, olsShieldVerdicts, shieldData]);

    // ── Show/hide ARP markers based on vis state ───────────────────────────────
    useEffect(() => {
        if (!mapInst.current) return;
        Object.entries(arpMarkersRef.current).forEach(([icao, marker]) => {
            const state = getAeroState(icao);
            const allOff = !state.checked && !state.indeterminate;
            if (allOff) {
                if (mapInst.current.hasLayer(marker)) mapInst.current.removeLayer(marker);
            } else {
                if (!mapInst.current.hasLayer(marker)) marker.addTo(mapInst.current);
            }
        });
    }, [vis, surfaceData]); // eslint-disable-line

    const [capturingIcao, setCapturingIcao] = useState(null); // { icao, type }

    // Capture the map exactly as-is — no repositioning, no coordinate adjustment.
    // Composites tile images + SVG overlay + markers onto a canvas, then POSTs to backend.
    const captureAerodrome = useCallback(async (icao, type = 'ols') => {
        if (!mapRef.current) return;
        setCapturingIcao({ icao, type });
        try {
            const container = mapRef.current;
            const containerRect = container.getBoundingClientRect();
            const w = Math.round(containerRect.width);
            const h = Math.round(containerRect.height);

            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#07111e';
            ctx.fillRect(0, 0, w, h);

            // Draw base map tiles
            const tileEls = [...container.querySelectorAll('.leaflet-tile:not(.leaflet-tile-loading)')];
            await Promise.all(tileEls.map(async tile => {
                if (!tile.src) return;
                const tRect = tile.getBoundingClientRect();
                const tx = Math.floor(tRect.left - containerRect.left);
                const ty = Math.floor(tRect.top - containerRect.top);
                const tw = Math.ceil(tRect.width) + 1;
                const th = Math.ceil(tRect.height) + 1;
                try {
                    const resp = await fetch(tile.src, { mode: 'cors' });
                    if (!resp.ok) return;
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    await new Promise(resolve => {
                        const img = new Image();
                        img.onload = () => {
                            ctx.drawImage(img, tx, ty, tw, th);
                            URL.revokeObjectURL(url);
                            resolve();
                        };
                        img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                        img.src = url;
                    });
                } catch { }
            }));

            // Draw SVG overlay
            const svgEl = container.querySelector('.leaflet-overlay-pane svg');
            if (svgEl) {
                const svgRect = svgEl.getBoundingClientRect();
                const sx = Math.round(svgRect.left - containerRect.left);
                const sy = Math.round(svgRect.top - containerRect.top);
                const clone = svgEl.cloneNode(true);
                clone.style.transform = '';
                clone.style.left = '0';
                clone.style.top = '0';
                const svgStr = new XMLSerializer().serializeToString(clone);
                const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                await new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => {
                        ctx.drawImage(img, sx, sy, Math.round(svgRect.width), Math.round(svgRect.height));
                        URL.revokeObjectURL(url);
                        resolve();
                    };
                    img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                    img.src = url;
                });
            }

            // Draw HTML markers
            const markerEls = [...container.querySelectorAll('.leaflet-marker-icon')];
            for (const marker of markerEls) {
                const mRect = marker.getBoundingClientRect();
                const x = Math.round(mRect.left - containerRect.left);
                const y = Math.round(mRect.top - containerRect.top);
                const img = marker.querySelector('img');
                if (img && img.complete && img.naturalWidth) {
                    try { ctx.drawImage(img, x, y, Math.round(mRect.width), Math.round(mRect.height)); } catch {}
                }
            }

            const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
            const res = await fetch(`${API_BASE}/ols-surfaces/${icao}/screenshot?type=${type}&project_id=${projectId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ image: dataUrl }),
            });
            if (res.ok) {
                setCapturedIcaos(p => ({ ...p, [icao]: { ...(p[icao] || {}), [type]: true } }));
            }
        } catch (err) {
            console.error('Capture failed:', err);
        } finally {
            setCapturingIcao(null);
        }
    }, [setCapturedIcaos]);

    const clearOlsScreenshot = useCallback(async (icao, type = 'ols') => {
        try {
            await fetch(`${API_BASE}/ols-surfaces/${icao}/screenshot?type=${type}&project_id=${projectId}`, { method: 'DELETE' });
            setCapturedIcaos(prev => {
                const iStore = { ...(prev[icao] || {}) };
                delete iStore[type];
                return { ...prev, [icao]: iStore };
            });
        } catch (err) { console.error('Clear OLS screenshot error:', err); }
    }, [setCapturedIcaos, projectId]);

    const confirmReset = useCallback((icao) => {
        setResetIcao(icao);
    }, []);

    const executeReset = useCallback(() => {
        if (!resetIcao) return;
        setSurfaceData(prev => {
            const next = { ...prev };
            delete next[resetIcao];
            return next;
        });
        setVis(prev => {
            const next = { ...prev };
            delete next[resetIcao];
            return next;
        });
        setResetIcao(null);
    }, [resetIcao, setSurfaceData, setVis]);

    // Build table rows from local per-ICAO assessment results (flat rows)
    const olsObsLookup = {};
    obstacles.forEach(o => { olsObsLookup[o.id] = o; });

    const aeroGroups = new Map(); // icao → { icao, name, rows[] }
    Object.entries(localOlsResults).forEach(([icao, rows]) => {
        if (!aeroGroups.has(icao)) {
            const arp = arps.find(a => a.icao === icao);
            aeroGroups.set(icao, { icao, name: arp?.name || icao, rows: [] });
        }
        rows.forEach(r => {
            const obs = olsObsLookup[r.obstacle_id];
            const penetration = r.margin != null ? parseFloat((-r.margin).toFixed(2)) : null;
            const isPenetrated = r.status === 'penetrated';
            aeroGroups.get(icao).rows.push({
                obsName:     r.obstacle_name,
                aerodrome:   r.aerodrome,
                lat:         obs?.latitude,
                lon:         obs?.longitude,
                elev:        obs?.elevation_m,
                runway:      r.runway || '—',
                surface:     r.surface,
                surfaceType: r.surfaceType || '',
                surfElev:    r.surf_elev_amsl != null ? parseFloat(r.surf_elev_amsl).toFixed(2) : null,
                obsElev:     r.obs_elev != null ? parseFloat(r.obs_elev).toFixed(2) : null,
                penetration,
                status:      r.status,
                comments:    isPenetrated && penetration != null ? (penetration <= 5 ? 'Minor' : 'Major') : '',
            });
        });
    });
    const tableRows = [...aeroGroups.values()];

    // Mark the controlling obstacle (highest penetration) per runway+surface group
    tableRows.forEach(group => {
        const worstBySurface = new Map();
        group.rows.forEach(r => {
            if (r.penetration == null) return;
            const key = `${r.runway}__${r.surface}`;
            const cur = worstBySurface.get(key);
            if (!cur || r.penetration > cur.penetration) worstBySurface.set(key, r);
        });
        worstBySurface.forEach(r => { r.isControlling = true; });
    });

    // Pre-initialise vis for every valid aerodrome so markers show by default
    useEffect(() => {
        if (validArps.length === 0) return;
        setVis(p => {
            const next = { ...p };
            validArps.forEach(arp => {
                if (!next[arp.icao]) next[arp.icao] = defaultVis();
            });
            return next;
        });
    }, [validArps]); // eslint-disable-line

    // ── Hierarchical check-state helpers ────────────────────────────────────────
    // All aerodromes currently shown on the map — the pre-init effect above
    // gives every validArp a vis[] entry even before its surfaces are loaded.
    // The grand "toggle all" must cover all of them, not just the ones with
    // loaded surface data, otherwise any aerodrome that hasn't had "Load Data"
    // clicked is silently unaffected by it and can never be switched off (and
    // since it was never really off, toggling a *different* aerodrome back on
    // makes it look like that one "came back on" too, when it was never gone).
    const allIcaos = Object.keys(vis);

    function getAeroState(icao) {
        const v = vis[icao];
        if (!v) return { checked: true, indeterminate: false };
        const data = surfaceData[icao];
        const checks = [];
        // Aerodrome-level
        AERODROME_SURFACES.forEach(k => { if (true) checks.push(!!v[k]); });
        if ((data?.runway_pairs || []).some(p => p.surfaces?.transitional)) checks.push(!!v.transitional);
        // Per-pair
        (data?.runway_pairs || []).forEach(pair => {
            const pv = v.pairs?.[pair.pair_id] || {};
            if (pair.surfaces?.strip) checks.push(!!pv.strip);
            [pair.desig1, pair.desig2].filter(Boolean).forEach(desig => {
                ['approach', 'departure'].forEach(k => {
                    if (pair.surfaces?.[k]) checks.push(!!(pv[desig]?.[k]));
                });
            });
            if (pair.surfaces?.ofz) {
                Object.keys(pair.surfaces.ofz).forEach(desig => {
                    const ov = pv.ofz?.[desig] || {};
                    OFZ_SURFACES.forEach(k => { if (pair.surfaces.ofz[desig][k]) checks.push(!!ov[k]); });
                });
            }
        });
        const n = checks.filter(Boolean).length;
        if (n === 0) return { checked: false, indeterminate: false };
        if (n === checks.length) return { checked: true, indeterminate: false };
        return { checked: false, indeterminate: true };
    }

    const grandState = (() => {
        if (allIcaos.length === 0) return { checked: false, indeterminate: false };
        const states = allIcaos.map(getAeroState);
        const nOn = states.filter(s => s.checked).length;
        const nSome = states.filter(s => s.checked || s.indeterminate).length;
        if (nOn === allIcaos.length) return { checked: true, indeterminate: false };
        if (nSome === 0) return { checked: false, indeterminate: false };
        return { checked: false, indeterminate: true };
    })();

    function toggleAeroAll(icao) {
        setVis(p => {
            const data = surfaceData[icao];
            const on = !getAeroState(icao).checked;
            const newPairs = {};
            (data?.runway_pairs || []).forEach(pair => {
                const pv = { strip: on, ofz: {} };
                [pair.desig1, pair.desig2].filter(Boolean).forEach(d => { pv[d] = { approach: on, departure: on }; });
                if (pair.surfaces?.ofz) {
                    Object.keys(pair.surfaces.ofz).forEach(desig => {
                        pv.ofz[desig] = Object.fromEntries(OFZ_SURFACES.map(k => [k, on]));
                    });
                }
                newPairs[pair.pair_id] = pv;
            });
            return { ...p, [icao]: { ihs: on, conical: on, ohs: on, transitional: on, pairs: newPairs } };
        });
    }

    function toggleGrandAll() {
        const forceOn = !grandState.checked;
        setVis(p => {
            const newVis = { ...p };
            allIcaos.forEach(icao => {
                const data = surfaceData[icao];
                const newPairs = {};
                (data?.runway_pairs || []).forEach(pair => {
                    const pv = { strip: forceOn, ofz: {} };
                    [pair.desig1, pair.desig2].filter(Boolean).forEach(d => { pv[d] = { approach: forceOn, departure: forceOn }; });
                    if (pair.surfaces?.ofz) {
                        Object.keys(pair.surfaces.ofz).forEach(desig => {
                            pv.ofz[desig] = Object.fromEntries(OFZ_SURFACES.map(k => [k, forceOn]));
                        });
                    }
                    newPairs[pair.pair_id] = pv;
                });
                newVis[icao] = { ihs: forceOn, conical: forceOn, ohs: forceOn, transitional: forceOn, pairs: newPairs };
            });
            return newVis;
        });
    }

    // ── Visibility togglers ──────────────────────────────────────────────────
    function toggleAeroSurf(icao, key) {
        setVis(p => ({ ...p, [icao]: { ...p[icao], [key]: !p[icao]?.[key] } }));
    }
    function toggleTransitional(icao) {
        setVis(p => ({ ...p, [icao]: { ...p[icao], transitional: !p[icao]?.transitional } }));
    }
    function toggleTransDebug(icao) {
        setVis(p => ({ ...p, [icao]: { ...p[icao], transDebug: !p[icao]?.transDebug } }));
    }
    function toggleStrip(icao, pairId) {
        setVis(p => {
            const v = p[icao] || {};
            const pv = v.pairs?.[pairId] || {};
            return { ...p, [icao]: { ...v, pairs: { ...v.pairs, [pairId]: { ...pv, strip: !pv.strip } } } };
        });
    }
    function toggleDesigSurf(icao, pairId, desig, key) {
        setVis(p => {
            const v = p[icao] || {};
            const pv = v.pairs?.[pairId] || {};
            const dv = pv[desig] || {};
            return { ...p, [icao]: { ...v, pairs: { ...v.pairs, [pairId]: { ...pv, [desig]: { ...dv, [key]: !dv[key] } } } } };
        });
    }
    function toggleOfzSurf(icao, pairId, desig, key) {
        setVis(p => {
            const v = p[icao] || {};
            const pv = v.pairs?.[pairId] || {};
            const ofz = { ...pv.ofz, [desig]: { ...pv.ofz?.[desig], [key]: !pv.ofz?.[desig]?.[key] } };
            return { ...p, [icao]: { ...v, pairs: { ...v.pairs, [pairId]: { ...pv, ofz } } } };
        });
    }

    async function handleExportAixm() {
        if (!projectId) return;
        setExportingAixm(true);
        try {
            const resp = await fetch(`${API_BASE}/projects/${projectId}/export/aixm?surfaces=ols`);
            if (!resp.ok) {
                const body = await resp.json().catch(() => ({}));
                throw new Error(body.detail || `Export failed (${resp.status})`);
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            // The server resolves the real aerodrome(s) from obstacle
            // proximity, not project.aerodrome (often stale/left at its
            // default 'FAOR') — read its filename back off the response
            // rather than reconstructing one from that field here too.
            const cd = resp.headers.get('content-disposition') || '';
            const nameMatch = cd.match(/filename="?([^";]+)"?/);
            link.download = nameMatch ? nameMatch[1] : `project_${projectId}_export_OLS_AIXM51.xml`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export AIXM:', err);
            alert(err.message || 'Failed to export AIXM. Please check your network connection and try again.');
        } finally {
            setExportingAixm(false);
        }
    }

    const renderResultsContent = () => {
        const ofzSurfaces = new Set(['inner_approach', 'balked_landing', 'inner_transitional']);
        // Omni surfaces (aerodrome-wide, not tied to a runway end) get their
        // own dedicated tabs instead of only showing under ALL — an obstacle
        // can penetrate IHS/conical/OHS independently of whether it also
        // penetrates a directional (approach/departure/transitional/strip)
        // surface, since the engine now reports every intersecting surface.
        const OMNI_TAB_LABELS = { ihs: 'Inner Horizontal', conical: 'Conical', ohs: 'Outer Horizontal' };
        const allRows = tableRows.flatMap(g => g.rows);
        const hasOfz  = allRows.some(r => ofzSurfaces.has((r.surfaceType || '').toLowerCase()));
        const omniTypesPresent = ['ihs', 'conical', 'ohs'].filter(t =>
            allRows.some(r => (r.surfaceType || '').toLowerCase() === t));
        const rwySet  = new Set(
            allRows
                .filter(r => r.runway && r.runway !== '—' && !ofzSurfaces.has((r.surfaceType || '').toLowerCase()))
                .map(r => r.runway)
        );
        const tabs = [
            'ALL',
            ...(hasOfz ? ['OFZ'] : []),
            ...omniTypesPresent.map(t => OMNI_TAB_LABELS[t]),
            ...[...rwySet].sort().map(rwy => `RWY ${rwy}`),
        ];
        const activeTab = tabs.includes(olsTab) ? olsTab : 'ALL';

        const filteredGroups = tableRows.map(g => ({
            ...g,
            rows: g.rows.filter(r => {
                if (activeTab === 'ALL') return true;
                if (activeTab === 'OFZ') return ofzSurfaces.has((r.surfaceType || '').toLowerCase());
                const omniLabel = OMNI_TAB_LABELS[(r.surfaceType || '').toLowerCase()];
                if (omniLabel) return omniLabel === activeTab;
                const rwyLabel = `RWY ${r.runway}`;
                return rwyLabel === activeTab && !ofzSurfaces.has((r.surfaceType || '').toLowerCase());
            }),
        })).filter(g => g.rows.length > 0);

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                {/* Tab pills */}
                <div style={{ display: 'flex', gap: 4, padding: '6px 10px', borderBottom: `1px solid ${T.border}33`, flexWrap: 'wrap', flexShrink: 0, justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {tabs.map(tab => {
                            const isActive = tab === activeTab;
                            const isOfz   = tab === 'OFZ';
                            const accent  = isOfz ? '#FF9F43' : T.blue;
                            return (
                                <button
                                    key={tab}
                                    id={`ols-tab-${tab.replace(/\s+/g, '-')}`}
                                    onClick={() => setOlsTab(tab)}
                                    style={{
                                        padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                                        border: `1px solid ${isActive ? accent : T.border}`,
                                        background: isActive ? accent + '22' : 'transparent',
                                        color: isActive ? accent : T.muted,
                                        cursor: 'pointer', transition: 'all 0.15s',
                                    }}
                                >
                                    {tab}
                                </button>
                            );
                        })}
                    </div>
                    {allRows.length > 0 && (
                        <button
                            onClick={() => {
                                const headers = ["Obstacle ID", "Aerodrome", "Latitude", "Longitude", "Elevation (m)", "Surface", "Surface Elevation (m)", "Penetration (m)", "RWY", "Comments"];
                                const rows = allRows.map(r => [
                                    r.obsName,
                                    r.aerodrome,
                                    toDMS(r.lat, true),
                                    toDMS(r.lon, false),
                                    r.elev != null ? r.elev : '-',
                                    r.surface,
                                    r.surfElev != null ? r.surfElev : '-',
                                    r.penetration != null ? r.penetration.toFixed(2) : '-',
                                    r.runway,
                                    r.comments || ''
                                ]);
                                exportToCsv(headers, rows, `OLS_Assessment_Results.csv`);
                            }}
                            style={{
                                padding: '3px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                                background: T.blue, color: T.white, fontSize: 10, fontWeight: 700,
                                display: 'flex', alignItems: 'center', gap: 4, marginRight: 8
                            }}
                        >
                            📥 Export CSV
                        </button>
                    )}
                </div>

                {/* Table */}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                            <tr style={{ background: T.bg0, position: 'sticky', top: 0, zIndex: 1 }}>
                                {['Obstacle ID', 'Aerodrome', 'Latitude', 'Longitude', 'Elevation (m)', 'Surface', 'Surface Elevation (m)', 'Penetration (m)', 'RWY', 'Comments'].map(h => (
                                    <th key={h} style={{ padding: '6px 12px', textAlign: 'left', color: T.muted, fontWeight: 600, borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredGroups.length > 0 ? filteredGroups.map(group => (
                                <React.Fragment key={group.icao}>
                                    <tr>
                                        <td colSpan={10} style={{ padding: '6px 12px', background: 'rgba(58,138,196,0.12)', color: T.blue, fontWeight: 800, fontSize: 11, letterSpacing: 0.5, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}33` }}>
                                            {group.icao} – {group.name}
                                        </td>
                                    </tr>
                                    {group.rows.map((r, i) => (
                                        <tr key={i} style={{ borderBottom: `1px solid ${T.border}22`, background: r.status === 'penetrated' ? 'rgba(232,64,64,0.10)' : 'rgba(46,201,139,0.06)' }}>
                                            <td style={{ padding: '5px 12px', color: T.white, fontWeight: r.isControlling ? 700 : 400 }}>
                                                {r.obsName} {r.isControlling && <span style={{ color: T.blue, fontSize: 9 }}>[Controlling]</span>}
                                            </td>
                                            <td style={{ padding: '5px 12px', color: T.blue, fontWeight: 700 }}>{r.aerodrome}</td>
                                            <td style={{ padding: '5px 12px', color: T.white, whiteSpace: 'nowrap' }}>{toDMS(r.lat, true)}</td>
                                            <td style={{ padding: '5px 12px', color: T.white, whiteSpace: 'nowrap' }}>{toDMS(r.lon, false)}</td>
                                            <td style={{ padding: '5px 12px', color: T.muted }}>{r.elev != null ? r.elev : '—'}</td>
                                            <td style={{ padding: '5px 12px', color: T.white }}>{r.surface}</td>
                                            <td style={{ padding: '5px 12px', color: T.muted }}>{r.surfElev != null ? r.surfElev : '—'}</td>
                                            <td style={{ padding: '5px 12px', color: r.penetration == null ? T.muted : r.penetration > 0 ? T.danger : T.safe, fontWeight: r.penetration != null ? 700 : 400 }}>
                                                {r.penetration != null ? r.penetration.toFixed(2) : '—'}
                                            </td>
                                            <td style={{ padding: '5px 12px', color: T.muted }}>{r.runway}</td>
                                            <td style={{ padding: '5px 12px', color: r.comments === 'Major' ? T.danger : r.comments === 'Minor' ? '#f5a623' : T.muted, fontWeight: 700 }}>{r.comments || '—'}</td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            )) : (
                                <tr>
                                    <td colSpan={10} style={{ padding: '20px 12px', textAlign: 'center', color: T.muted }}>
                                        {tableRows.length === 0 ? 'Run assessment to see OLS results' : `No results for ${activeTab}`}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

            {/* ── Left panel ─────────────────────────────────────────────── */}
            <div style={{ width: 270, flexShrink: 0, background: T.bg1, backdropFilter: 'blur(var(--blur-md))', WebkitBackdropFilter: 'blur(var(--blur-md))', borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                {/* Header — grandparent checkbox */}
                <div style={{ padding: '10px 14px', borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {validArps.length > 0 && (
                            <input
                                type="checkbox"
                                ref={el => { if (el) el.indeterminate = grandState.indeterminate; }}
                                checked={grandState.checked}
                                onChange={toggleGrandAll}
                                title="Toggle all surfaces for all aerodromes"
                                style={{ accentColor: T.blue, cursor: 'pointer', flexShrink: 0 }}
                            />
                        )}
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, fontWeight: 800, color: T.white }}>✈ OLS Surface Tree</div>
                            <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>Annex 14 · 7 surfaces per aerodrome</div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                        <button
                            onClick={() => setShowOlsParameters(true)}
                            style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #a78bfa44', cursor: 'pointer', background: '#a78bfa11', color: '#a78bfa', fontSize: 10, fontWeight: 700 }}
                            title="View and export the OLS parameters applied per runway"
                        >
                            OLS PARAMS
                        </button>
                        <button
                            onClick={() => setShowIhsThreshold(true)}
                            style={{ padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.blue}44`, cursor: 'pointer', background: `${T.blue}11`, color: T.blue, fontSize: 10, fontWeight: 700 }}
                            title="Compare IHS elevation: ARP basis vs runway threshold basis"
                        >
                            IHS/THR
                        </button>
                        <button
                            onClick={() => setShowKmlExport(true)}
                            style={{ padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.safe}44`, cursor: 'pointer', background: `${T.safe}11`, color: T.safe, fontSize: 10, fontWeight: 700 }}
                            title="Export KMZ"
                        >
                            KMZ
                        </button>
                        <button
                            onClick={handleExportAixm}
                            disabled={exportingAixm}
                            style={{ padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.blue}44`, cursor: exportingAixm ? 'default' : 'pointer', background: `${T.blue}11`, color: T.blue, fontSize: 10, fontWeight: 700, opacity: exportingAixm ? 0.6 : 1 }}
                            title="Export obstacles, aerodrome data, waypoints and OLS surfaces (ObstacleArea) as AIXM 5.1"
                        >
                            {exportingAixm ? '⏳…' : '📤 AIXM'}
                        </button>
                    </div>
                </div>

                {/* Aerodrome list */}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {validArps.length === 0 && (
                        <div style={{ padding: 16, fontSize: 12, color: T.muted }}>No aerodromes within 15 km of obstacles.</div>
                    )}

                    {validArps.map((arp, idx) => {
                        const icao = arp.icao;
                        const data = surfaceData[icao];
                        const isLoading = loadingIcao[icao];
                        const err = errorIcao[icao];
                        const isExpanded = expanded[icao];
                        const v = vis[icao] || {};
                        const aeroState = getAeroState(icao);

                        return (
                            <div key={idx} style={{ borderBottom: `1px solid ${T.border}22` }}>
                                {/* Parent row */}
                                <div style={{ padding: '8px 12px', cursor: 'pointer', background: isExpanded ? 'rgba(58,138,196,0.06)' : 'transparent' }}
                                    onClick={() => setExpanded(p => ({ ...p, [icao]: !isExpanded }))}>
                                    
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {/* Parent checkbox — toggles all surfaces for this aerodrome */}
                                        <input
                                            type="checkbox"
                                            ref={el => { if (el) el.indeterminate = aeroState.indeterminate; }}
                                            checked={aeroState.checked}
                                            onChange={e => { e.stopPropagation(); toggleAeroAll(icao); }}
                                            onClick={e => e.stopPropagation()}
                                            title={`Toggle all ${icao} surfaces`}
                                            style={{ accentColor: T.blue, cursor: 'pointer', flexShrink: 0 }}
                                        />
                                        <span style={{ color: T.muted, fontSize: 10 }}>{isExpanded ? '▼' : '▶'}</span>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 12, fontWeight: 800, color: T.blue }}>{icao}</div>
                                            <div style={{ fontSize: 10, color: T.muted }}>{arp.name} · {arp.distance_km} km</div>
                                        </div>
                                    </div>

                                    {/* Action Buttons - 4 Grid Style */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 10, paddingLeft: 22 }} onClick={e => e.stopPropagation()}>
                                        <button
                                            onClick={() => loadSurfaces(icao)}
                                            disabled={isLoading}
                                            style={{ padding: '4px 8px', fontSize: 10, fontWeight: 700, borderRadius: 5, border: 'none', cursor: 'pointer', background: data ? 'rgba(46,201,139,0.15)' : T.blue, color: data ? T.safe : '#fff', width: '100%', textAlign: 'center' }}>
                                            {isLoading ? '…' : data ? '↻ Reload' : 'Load Data'}
                                        </button>

                                        {data && (
                                            <>
                                                {/* Screenshot Capture Grid F1-F7 */}
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 4, marginTop: 4 }}>
                                                    {['f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7'].map(fKey => {
                                                        const isCaptured = capturedIcaos[icao]?.[fKey];
                                                        const isCapturing = capturingIcao?.icao === icao && capturingIcao?.type === fKey;
                                                        return (
                                                            <div key={fKey} style={{ display: 'flex', background: 'rgba(255,255,255,0.03)', borderRadius: 4, border: `1px solid ${T.border}44`, overflow: 'hidden' }}>
                                                                <button
                                                                    onClick={() => captureAerodrome(icao, fKey)}
                                                                    disabled={capturingIcao?.icao === icao}
                                                                    title={`Capture ${fKey.toUpperCase()} for ${icao}`}
                                                                    style={{ flex: 1, padding: '4px 0', fontSize: 10, fontWeight: 700, border: 'none', cursor: 'pointer', background: isCaptured ? 'rgba(46,201,139,0.2)' : 'transparent', color: isCaptured ? T.safe : T.muted }}>
                                                                    {isCapturing ? '⌛' : `📷 ${fKey.toUpperCase()}`}
                                                                </button>
                                                                {isCaptured && (
                                                                    <button onClick={() => clearOlsScreenshot(icao, fKey)} style={{ background: 'transparent', borderLeft: `1px solid ${T.border}44`, borderTop: 'none', borderRight: 'none', borderBottom: 'none', color: T.danger, padding: '0 4px', cursor: 'pointer', fontSize: 11 }}>×</button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                <button title="Reset Assessment" onClick={() => confirmReset(icao)} style={{
                                                    padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.border}`,
                                                    background: 'transparent', color: T.danger, cursor: 'pointer', fontSize: 11, width: '100%'
                                                }}>🗑️ Reset</button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {err && <div style={{ padding: '4px 12px', fontSize: 10, color: T.danger }}>{err}</div>}

                                {/* ── Tree body ── */}
                                {isExpanded && data && (
                                    <div style={{ paddingLeft: 20, paddingBottom: 6 }}>

                                        {/* ── AERODROME section ─────────────────── */}
                                        <div style={{ padding: '4px 8px', fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: 1 }}>AERODROME</div>

                                        {/* Transitional — aerodrome-wide toggle (shown only if any pair has one) */}
                                        {(data.runway_pairs || []).some(pair => pair.surfaces?.transitional) && (() => {
                                            const cfg = surfColors.transitional;
                                            const checked = !!v.transitional;
                                            const debugChecked = !!v.transDebug;
                                            return (
                                                <>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 8px', cursor: 'pointer' }}>
                                                        <input type="checkbox" checked={checked} onChange={() => toggleTransitional(icao)} style={{ accentColor: cfg.color }} />
                                                        <div style={{ width: 10, height: 10, borderRadius: 2, background: cfg.color, opacity: 0.8 }} />
                                                        <span style={{ fontSize: 11, color: checked ? T.white : T.muted }}>{cfg.label}</span>
                                                    </label>
                                                    {checked && (
                                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 8px 3px 26px', cursor: 'pointer' }}
                                                               title="F / G2 / G / H / H2 / C construction points">
                                                            <input type="checkbox" checked={debugChecked} onChange={() => toggleTransDebug(icao)} style={{ accentColor: '#FF3B9A' }} />
                                                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF3B9A' }} />
                                                            <span style={{ fontSize: 10.5, color: debugChecked ? T.white : T.muted }}>Construction points (F/G2/G/H/H2/C)</span>
                                                        </label>
                                                    )}
                                                </>
                                            );
                                        })()}

                                        {/* IHS · Conical · OHS */}
                                        {AERODROME_SURFACES.map(key => {
                                            const cfg = surfColors[key];
                                            const checked = !!v[key];
                                            return (
                                                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 8px', cursor: 'pointer' }}>
                                                    <input type="checkbox" checked={checked} onChange={() => toggleAeroSurf(icao, key)} style={{ accentColor: cfg.color }} />
                                                    <div style={{ width: 10, height: 10, borderRadius: 2, background: cfg.color, opacity: 0.8 }} />
                                                    <span style={{ fontSize: 11, color: checked ? T.white : T.muted }}>{cfg.label}</span>
                                                </label>
                                            );
                                        })}

                                        {/* ── Per-RWY sections (one per designator) ── */}
                                        {(data.runway_pairs || []).flatMap((pair, pi) =>
                                            [pair.desig1, pair.desig2].filter(Boolean).map((desig, di) => {
                                                const pv = v.pairs?.[pair.pair_id] || {};
                                                const dv = pv[desig] || {};
                                                const rawCls = di === 0 ? (pair.cls1 || '') : (pair.cls2 || '');
                                                const clsLabel = rawCls === 'PA' ? pair.classification
                                                    : rawCls === 'NP' ? 'Non-Precision'
                                                    : rawCls === 'NI' ? 'Non-Instrument'
                                                    : pair.classification;
                                                const ofzForDesig = pair.surfaces?.ofz?.[desig];

                                                return (
                                                    <div key={`${pi}-${di}`} style={{ marginTop: 6 }}>
                                                        {/* RWY header */}
                                                        <div style={{ padding: '4px 8px', fontSize: 10, fontWeight: 700, color: T.blue }}>
                                                            RWY {desig}
                                                            <span style={{ fontWeight: 400, color: T.muted }}> · {clsLabel} · Code {pair.code}</span>
                                                        </div>

                                                        {/* Strip — shared, shown under each designator but controls same polygon */}
                                                        {(() => {
                                                            const cfg = surfColors.strip;
                                                            const checked = !!pv.strip;
                                                            const hasGeom = !!pair.surfaces?.strip;
                                                            return (
                                                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 8px', cursor: hasGeom ? 'pointer' : 'default', opacity: hasGeom ? 1 : 0.35 }}>
                                                                    <input type="checkbox" checked={checked && hasGeom} disabled={!hasGeom} onChange={() => toggleStrip(icao, pair.pair_id)} style={{ accentColor: cfg.color }} />
                                                                    <div style={{ width: 10, height: 10, borderRadius: 2, background: cfg.color, opacity: 0.8 }} />
                                                                    <span style={{ fontSize: 11, color: (checked && hasGeom) ? T.white : T.muted }}>{cfg.label}</span>
                                                                </label>
                                                            );
                                                        })()}

                                                        {/* Approach · Departure (per designator) */}
                                                        {['approach', 'departure'].map(key => {
                                                            const cfg = surfColors[key];
                                                            const checked = !!dv[key];
                                                            const hasGeom = !!pair.surfaces?.[key];
                                                            return (
                                                                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 8px', cursor: hasGeom ? 'pointer' : 'default', opacity: hasGeom ? 1 : 0.35 }}>
                                                                    <input type="checkbox" checked={checked && hasGeom} disabled={!hasGeom} onChange={() => toggleDesigSurf(icao, pair.pair_id, desig, key)} style={{ accentColor: cfg.color }} />
                                                                    <div style={{ width: 10, height: 10, borderRadius: 2, background: cfg.color, opacity: 0.8 }} />
                                                                    <span style={{ fontSize: 11, color: (checked && hasGeom) ? T.white : T.muted }}>{cfg.label}</span>
                                                                </label>
                                                            );
                                                        })}

                                                        {/* OFZ — only for the designator that has it */}
                                                        {ofzForDesig && (
                                                            <div style={{ marginTop: 2, paddingLeft: 8 }}>
                                                                <div style={{ padding: '2px 8px', fontSize: 10, fontWeight: 700, color: '#FF9F43' }}>OFZ</div>
                                                                {OFZ_SURFACES.map(key => {
                                                                    const cfg = surfColors[key];
                                                                    const checked = !!pv.ofz?.[desig]?.[key];
                                                                    const hasGeom = !!ofzForDesig[key];
                                                                    return (
                                                                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 8px', cursor: hasGeom ? 'pointer' : 'default', opacity: hasGeom ? 1 : 0.35 }}>
                                                                            <input type="checkbox" checked={checked && hasGeom} disabled={!hasGeom} onChange={() => toggleOfzSurf(icao, pair.pair_id, desig, key)} style={{ accentColor: cfg.color }} />
                                                                            <div style={{ width: 10, height: 10, borderRadius: 2, background: cfg.color, opacity: 0.8 }} />
                                                                            <span style={{ fontSize: 11, color: (checked && hasGeom) ? T.white : T.muted }}>{cfg.label}</span>
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Screenshot Legend */}
                    {validArps.length > 0 && (
                        <div style={{ padding: '12px 16px', borderTop: `1px solid ${T.border}22`, background: 'rgba(0,0,0,0.1)' }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: T.white, marginBottom: 6 }}>📷 Screenshot Legend</div>
                            <ul style={{ fontSize: 10, color: T.muted, listStyle: 'none', padding: 0, margin: 0, lineHeight: 1.6 }}>
                                <li><strong>F1</strong> - OLS screenshot</li>
                                <li><strong>F2</strong> - OFZ screenshot</li>
                                <li><strong>F3</strong> - Obstacle location</li>
                                <li><strong>F4</strong> - Aerodrome surfaces</li>
                                <li><strong>F5</strong> - Runway surfaces (Strip, Transitional)</li>
                                <li><strong>F6</strong> - Runway surfaces (Take Off, Approach)</li>
                                <li><strong>F7</strong> - OLS + OBSTACLES</li>
                            </ul>
                        </div>
                    )}
                </div>

                {/* Legend */}
                <div style={{ padding: '10px 14px', borderTop: `1px solid ${T.border}`, fontSize: 10 }}>
                    <div style={{ color: T.muted, marginBottom: 6, fontWeight: 700 }}>SURFACE LEGEND</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 8px' }}>
                        {Object.entries(surfColors).map(([key, cfg]) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <div style={{ width: 10, height: 10, borderRadius: 2, background: cfg.color, flexShrink: 0 }} />
                                <span style={{ color: T.muted }}>{cfg.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Right: map + table ────────────────────────────────────── */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>

                {/* Map controls */}
                <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, display: 'flex', gap: 6, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 6, border: `1px solid ${T.border}`, backdropFilter: 'blur(8px)' }}>
                    {Object.entries(TILE_LAYERS).map(([key, layer]) => (
                        <button key={key} onClick={() => setMapMode(key)} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', background: mapMode === key ? T.blue : 'transparent', color: mapMode === key ? '#fff' : T.muted, fontSize: 11, fontWeight: mapMode === key ? 700 : 400 }}>{layer.label}</button>
                    ))}
                </div>

                {/* Shielding control */}
                <ShieldControl top={92} shieldOn={shieldOn} toggleShield={toggleShield} shieldData={shieldData} shieldLoading={shieldLoading} shieldError={shieldError} projectMaxTotalElev={projectMaxTotalElev} projectPenetratingMaxTotalElev={projectPenetratingMaxTotalElev} radiusKm={radiusKm} setRadiusKm={setRadiusKm} exportCsv={exportCsv}
                    penetratingCount={olsShieldRealPenetration.size}
                    onExportPenetrating={() => {
                        const byId = new Map();
                        [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
                         ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
                            byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
                        });
                        const entries = [];
                        olsShieldRealPenetration.forEach((penList, obsId) => {
                            const r = byId.get(obsId);
                            if (!r) return;
                            penList.forEach(v => entries.push({
                                r,
                                surfaceLabel: OLS_SURFACE_LABELS[v.surfaceType] + (v.runway ? ` RWY ${v.runway}` : ''),
                                penetrationAmount: (-v.margin).toFixed(2),
                            }));
                        });
                        exportSurfacePenetratingCsv(entries, radiusKm);
                    }} />
                {shieldOn && hasOlsInclinedImpact && (
                    <div style={{ position: 'absolute', top: 226, right: 12, zIndex: 1000, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 8, border: '1px solid #FFB02055', backdropFilter: 'blur(8px)', maxWidth: 260, fontSize: 10, color: T.muted, lineHeight: 1.5 }}>
                        <div style={{ color: '#FFB020', fontWeight: 700, marginBottom: 2 }}>🛫 Inclined Surfaces (real check)</div>
                        <div style={{ marginBottom: 4 }}>
                            Project impact on: {[...new Set(Object.keys(olsSurfaceImpacts).map(k => k.split('_')[0]))].map(t => OLS_SURFACE_LABELS[t]).join(', ')}
                        </div>
                        {olsShieldLoading ? 'Checking real penetration…' : (
                            olsShieldVerdicts.size > 0
                                ? <span><b style={{ color: '#FFB020' }}>{olsShieldVerdicts.size}</b> obstacle(s) confirmed to already penetrate as much or more than this project's worst case on at least one surface — not a new impact there.</span>
                                : 'No candidate already penetrates any of these surfaces at least as much as this project — flat elevation comparison alone would be misleading here.'
                        )}
                    </div>
                )}
                <MapMeasureTool map={mapState} top={52} tableOffset={resultsCollapsed ? 40 : 205} />
                <MapCoordinateViewer map={mapState} />

                {/* Map */}
                <div ref={mapRef} style={{ flex: 1 }} />

                {/* Results table */}
                <div style={{ height: resultsCollapsed ? 38 : resultsHeight, background: T.bg1, borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', transition: 'height 0.2s ease', overflow: 'hidden', flexShrink: 0 }}>
                        {!resultsCollapsed && <ResizeHandle onMouseDown={startResultsResize} />}
                    <div style={{ padding: '8px 20px', background: T.bg0, borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.white, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🏔</span> OLS Results
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            {!resultsPoppedOut && (
                                <button
                                    onClick={() => {
                                        setResultsPoppedOut(true);
                                        setResultsCollapsed(true);
                                    }}
                                    style={{
                                        background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                        display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600
                                    }}
                                    title="Pop out results to a movable window"
                                >
                                    🗖 Pop Out
                                </button>
                            )}
                            <button
                                onClick={() => setResultsCollapsed(!resultsCollapsed)}
                                style={{
                                    background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                    fontWeight: 600
                                }}
                            >
                                {resultsCollapsed ? '▲ Expand' : '▼ Collapse'}
                            </button>
                        </div>
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        {resultsPoppedOut ? (
                            <div style={{ padding: 20, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                                Results are popped out. 
                                <button 
                                    onClick={() => { setResultsPoppedOut(false); setResultsCollapsed(false); }} 
                                    style={{ background: 'none', border: 'none', color: T.blue, textDecoration: 'underline', cursor: 'pointer', marginLeft: 6, fontWeight: 600 }}
                                >
                                    Bring back
                                </button>
                            </div>
                        ) : renderResultsContent()}
                    </div>
                </div>
            </div>
            
            {showKmlExport && (
                <KmlExportModal onClose={() => setShowKmlExport(false)} arps={validArps} projectId={project?.id} />
            )}

            {showOlsParameters && (
                <OlsParametersModal onClose={() => setShowOlsParameters(false)} arps={validArps} />
            )}

            {showIhsThreshold && (
                <IhsThresholdModal onClose={() => setShowIhsThreshold(false)} arps={validArps} obstacles={obstacles} />
            )}

            {resetIcao && (
                <div className="dg-modal-overlay">
                    <div className="dg-modal" style={{ padding: 32, width: 420 }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>
                            Reset OLS Assessment
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 24 }}>
                            Are you sure you want to reset the OLS assessment for <b style={{ color: T.blue }}>{resetIcao}</b>? This will remove it from the report.
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button className="dg-btn dg-btn-ghost" onClick={() => setResetIcao(null)}>
                                Cancel
                            </button>
                            <button className="dg-btn" onClick={executeReset} style={{ background: T.danger, borderColor: T.danger, color: '#fff' }}>
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {resultsPoppedOut && (
                <DraggableModal
                    title={`${project?.name || ''} - OLS Results`}
                    onClose={() => setResultsPoppedOut(false)}
                    width={850}
                    height={500}
                >
                    {renderResultsContent()}
                </DraggableModal>
            )}
        </div>
    );
}
